<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { User, Lock, Eye, EyeOff, KeyRound, MessageSquare, ArrowLeft } from 'lucide-vue-next';
import { authService } from '@/services/authService';
import { isValidMobile } from '@/utils/validators';
import { useCountdown } from '@/composables/useCountdown';
import { showSuccess } from '@/utils/message';
import SliderVerify from '@/components/common/SliderVerify.vue';

const emit = defineEmits<{
  submit: [payload: { username: string; password: string }];
}>();

const username = ref('');
const password = ref('');
const showPassword = ref(false);
const userShake = ref(false);
const passShake = ref(false);
const hasError = ref(false);

// 修复登录审查#12：删除本地「5 次失败锁 15 分钟」内存计数——刷新页面即清零，形同虚设；
// 真锁定由后端 SecurityPolicyService（默认 5 次锁 30 分钟、策略可配）执行并透出文案。

function shakeBoth() {
  userShake.value = true;
  passShake.value = true;
  setTimeout(() => {
    userShake.value = false;
    passShake.value = false;
  }, 400);
}

const canSubmit = computed(() => username.value.trim().length > 0 && password.value.length > 0);

function handleSubmit() {
  if (!canSubmit.value) {
    if (!username.value.trim()) {
      userShake.value = true;
      setTimeout(() => (userShake.value = false), 400);
    }
    if (!password.value) {
      passShake.value = true;
      setTimeout(() => (passShake.value = false), 400);
    }
    return;
  }
  hasError.value = false;
  emit('submit', { username: username.value.trim(), password: password.value });
}

/** 登录失败反馈：仅做视觉抖动（真锁定在后端） */
function recordFailure() {
  hasError.value = true;
  shakeBoth();
}

defineExpose({ recordFailure });

// ==================== 忘记密码（修复登录审查#10：接通后端 /api/auth/admin/reset-password） ====================

const forgotMode = ref(false);
const forgotMobile = ref('');
const forgotCode = ref('');
const forgotNewPassword = ref('');
const showNewPassword = ref(false);
const forgotSliderVerified = ref(false);
const forgotCodeSent = ref(false);
const isSending = ref(false);
const isSubmitting = ref(false);
const forgotError = ref('');
const forgotSliderRef = ref<InstanceType<typeof SliderVerify> | null>(null);
const { remaining, isRunning, start } = useCountdown(60);

const forgotMobileError = computed(() => {
  if (!forgotMobile.value) return '';
  return isValidMobile(forgotMobile.value) ? '' : '请输入正确的11位手机号';
});

const canSendForgotCode = computed(
  () => isValidMobile(forgotMobile.value) && forgotSliderVerified.value && !isRunning.value && !isSending.value,
);

const canResetSubmit = computed(
  () =>
    isValidMobile(forgotMobile.value) &&
    forgotCodeSent.value &&
    forgotCode.value.length === 6 &&
    forgotNewPassword.value.length > 0,
);

// 手机号变化时重置滑块与发送状态（与 MobileLogin 同口径）
watch(forgotMobile, () => {
  if (forgotSliderVerified.value) {
    forgotSliderVerified.value = false;
    forgotSliderRef.value?.reset();
  }
  if (forgotCodeSent.value) {
    forgotCodeSent.value = false;
    forgotCode.value = '';
  }
});

async function handleSendForgotCode() {
  if (!canSendForgotCode.value) return;
  isSending.value = true;
  forgotError.value = '';
  try {
    const result = await authService.sendCode(forgotMobile.value);
    if (result.success) {
      forgotCodeSent.value = true;
      start();
    } else {
      forgotError.value = result.error || '验证码发送失败';
    }
  } finally {
    isSending.value = false;
  }
}

async function handleForgotSubmit() {
  if (!canResetSubmit.value || isSubmitting.value) return;
  isSubmitting.value = true;
  forgotError.value = '';
  try {
    const result = await authService.adminResetPassword(
      forgotMobile.value,
      forgotCode.value,
      forgotNewPassword.value,
    );
    if (result.success) {
      showSuccess('密码重置成功，请用新密码登录');
      // 回填手机号、退回登录表单，减少一步输入
      username.value = forgotMobile.value;
      password.value = '';
      exitForgotMode();
    } else {
      forgotError.value = result.message || '密码重置失败';
    }
  } finally {
    isSubmitting.value = false;
  }
}

function enterForgotMode() {
  forgotMode.value = true;
  forgotError.value = '';
}

function exitForgotMode() {
  forgotMode.value = false;
  forgotMobile.value = '';
  forgotCode.value = '';
  forgotNewPassword.value = '';
  forgotSliderVerified.value = false;
  forgotCodeSent.value = false;
  forgotSliderRef.value?.reset();
}
</script>

<template>
  <!-- 忘记密码面板 -->
  <div v-if="forgotMode" class="flex flex-col gap-5">
    <div class="flex items-center gap-2">
      <button
        type="button"
        class="flex items-center justify-center w-8 h-8 rounded-md text-[var(--color-caption)] hover:text-[var(--color-primary)] hover:bg-[var(--color-primary-light)] transition-colors"
        aria-label="返回登录"
        @click="exitForgotMode"
      >
        <ArrowLeft class="w-4 h-4" />
      </button>
      <h2 class="text-base font-medium" style="color: var(--color-title);">重置管理员密码</h2>
    </div>

    <div class="flex flex-col gap-1.5">
      <label for="forgot-mobile" class="text-sm font-medium" style="color: var(--color-title);">手机号</label>
      <div
        class="flex items-center gap-2 px-4 h-12 rounded-md border bg-white transition-all duration-200 focus-glow"
        :class="[forgotMobileError ? 'input-error' : 'border-[var(--color-border)]']"
      >
        <MessageSquare class="w-4 h-4 shrink-0" :class="forgotMobileError ? 'text-[var(--color-error)]' : 'text-[var(--color-caption)]'" />
        <input
          id="forgot-mobile"
          v-model="forgotMobile"
          type="tel"
          inputmode="numeric"
          maxlength="11"
          placeholder="请输入管理员手机号"
          aria-label="管理员手机号"
          class="flex-1 bg-transparent border-0 outline-none text-sm placeholder:text-[var(--color-caption)]"
        />
      </div>
      <transition name="error-fade">
        <span v-if="forgotMobileError" class="text-xs" style="color: var(--color-error);">{{ forgotMobileError }}</span>
      </transition>
    </div>

    <SliderVerify ref="forgotSliderRef" @success="forgotSliderVerified = true" />

    <div class="flex flex-col gap-1.5">
      <label for="forgot-code" class="text-sm font-medium" style="color: var(--color-title);">验证码</label>
      <div class="flex gap-3">
        <div
          class="flex min-w-0 flex-1 items-center gap-2 px-4 h-12 rounded-md border transition-all duration-200 focus-glow"
          :class="[forgotCodeSent ? 'bg-white' : 'bg-[var(--color-card-bg)]', 'border-[var(--color-border)]']"
        >
          <KeyRound class="w-4 h-4 shrink-0 text-[var(--color-caption)]" />
          <input
            id="forgot-code"
            v-model="forgotCode"
            type="text"
            inputmode="numeric"
            maxlength="6"
            :disabled="!forgotCodeSent"
            :placeholder="forgotCodeSent ? '请输入6位验证码' : '请先获取验证码'"
            aria-label="验证码"
            class="min-w-0 flex-1 bg-transparent border-0 outline-none text-sm placeholder:text-[var(--color-caption)] tracking-widest disabled:cursor-not-allowed"
          />
        </div>
        <button
          type="button"
          :disabled="!canSendForgotCode"
          :class="[
            'min-w-[120px] shrink-0 h-11 rounded-md text-sm font-medium transition-all duration-200 btn-press',
            canSendForgotCode
              ? 'text-white shadow-sm hover:shadow-md'
              : 'bg-[var(--color-card-bg)] text-[var(--color-caption)] cursor-not-allowed',
          ]"
          :style="canSendForgotCode ? 'background: var(--gradient-primary);' : ''"
          @click="handleSendForgotCode"
        >
          <span v-if="isSending">发送中…</span>
          <span v-else-if="isRunning" class="tabular-nums">{{ remaining }}秒后重试</span>
          <span v-else>获取验证码</span>
        </button>
      </div>
    </div>

    <div class="flex flex-col gap-1.5">
      <label for="forgot-new-password" class="text-sm font-medium" style="color: var(--color-title);">新密码</label>
      <div class="flex items-center gap-2 px-4 h-12 rounded-md border bg-white transition-all duration-200 focus-glow border-[var(--color-border)]">
        <Lock class="w-4 h-4 shrink-0 text-[var(--color-caption)]" />
        <input
          id="forgot-new-password"
          v-model="forgotNewPassword"
          :type="showNewPassword ? 'text' : 'password'"
          autocomplete="new-password"
          placeholder="请输入新密码（需满足密码强度策略）"
          aria-label="新密码"
          class="flex-1 bg-transparent border-0 outline-none text-sm placeholder:text-[var(--color-caption)]"
          @keyup.enter="handleForgotSubmit"
        />
        <button
          type="button"
          class="flex items-center justify-center w-7 h-7 rounded-md text-[var(--color-caption)] hover:text-[var(--color-primary)] transition-colors"
          :aria-label="showNewPassword ? '隐藏新密码' : '显示新密码'"
          @click="showNewPassword = !showNewPassword"
        >
          <EyeOff v-if="showNewPassword" class="w-4 h-4" />
          <Eye v-else class="w-4 h-4" />
        </button>
      </div>
    </div>

    <transition name="error-fade">
      <div v-if="forgotError" class="error-bar text-sm rounded-lg px-4 py-3" style="color: var(--color-error);" role="alert">
        {{ forgotError }}
      </div>
    </transition>

    <button
      type="button"
      :disabled="!canResetSubmit || isSubmitting"
      :class="[
        'mt-2 w-full h-11 rounded-md text-base font-medium text-white shadow-sm transition-all duration-200 btn-press',
        canResetSubmit && !isSubmitting ? 'hover:shadow-md cursor-pointer' : 'opacity-50 cursor-not-allowed',
      ]"
      style="background: var(--gradient-primary);"
      @click="handleForgotSubmit"
    >
      {{ isSubmitting ? '重置中…' : '重置密码' }}
    </button>
  </div>

  <!-- 管理员登录表单 -->
  <div v-else class="flex flex-col gap-5">
    <div class="flex flex-col gap-1.5">
      <label for="admin-username" class="text-sm font-medium" style="color: var(--color-title);">用户名 / 手机号</label>
      <div
        class="flex items-center gap-2 px-4 h-12 rounded-md border bg-white transition-all duration-200 focus-glow"
        :class="[
          (userShake || (hasError && !username)) ? 'input-error shake-error' : 'border-[var(--color-border)]',
        ]"
      >
        <User class="w-4 h-4 shrink-0" :class="userShake ? 'text-[var(--color-error)]' : 'text-[var(--color-caption)]'" />
        <input
          id="admin-username"
          v-model="username"
          type="text"
          autocomplete="username"
          placeholder="请输入用户名或手机号"
          aria-label="用户名或手机号"
          :aria-invalid="hasError ? 'true' : 'false'"
          class="flex-1 bg-transparent border-0 outline-none text-sm placeholder:text-[var(--color-caption)]"
          @keyup.enter="handleSubmit"
        />
      </div>
    </div>

    <div class="flex flex-col gap-1.5">
      <label for="admin-password" class="text-sm font-medium" style="color: var(--color-title);">密码</label>
      <div
        class="flex items-center gap-2 px-4 h-12 rounded-md border bg-white transition-all duration-200 focus-glow"
        :class="[
          (passShake || (hasError && !password)) ? 'input-error shake-error' : 'border-[var(--color-border)]',
        ]"
      >
        <Lock class="w-4 h-4 shrink-0" :class="passShake ? 'text-[var(--color-error)]' : 'text-[var(--color-caption)]'" />
        <input
          id="admin-password"
          v-model="password"
          :type="showPassword ? 'text' : 'password'"
          autocomplete="current-password"
          placeholder="请输入密码"
          aria-label="密码"
          :aria-invalid="hasError ? 'true' : 'false'"
          class="flex-1 bg-transparent border-0 outline-none text-sm placeholder:text-[var(--color-caption)]"
          @keyup.enter="handleSubmit"
        />
        <button
          type="button"
          class="flex items-center justify-center w-7 h-7 rounded-md text-[var(--color-caption)] hover:text-[var(--color-primary)] hover:bg-[var(--color-primary-light)] transition-colors"
          :aria-label="showPassword ? '隐藏密码' : '显示密码'"
          @click="showPassword = !showPassword"
        >
          <transition name="icon-fade" mode="out-in">
            <EyeOff v-if="showPassword" :key="'hide'" class="w-4 h-4" />
            <Eye v-else :key="'show'" class="w-4 h-4" />
          </transition>
        </button>
      </div>
    </div>

    <button
      type="button"
      :disabled="!canSubmit"
      :class="[
        'mt-2 w-full h-11 rounded-md text-base font-medium text-white shadow-sm transition-all duration-200 btn-press',
        canSubmit ? 'hover:shadow-md cursor-pointer' : 'opacity-50 cursor-not-allowed',
      ]"
      style="background: var(--gradient-primary);"
      @click="handleSubmit"
    >
      管理员登录
    </button>

    <div class="flex justify-end">
      <button
        type="button"
        class="text-sm hover:underline"
        style="color: var(--color-primary);"
        @click="enterForgotMode"
      >
        忘记密码？
      </button>
    </div>
  </div>
</template>

<style scoped>
.error-fade-enter-active,
.error-fade-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}
.error-fade-enter-from,
.error-fade-leave-to {
  opacity: 0;
  transform: translateY(-2px);
}

.icon-fade-enter-active,
.icon-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.icon-fade-enter-from,
.icon-fade-leave-to {
  opacity: 0;
  transform: scale(0.85);
}
</style>
